import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { BsArrowRight } from "react-icons/bs";
import { CiDeliveryTruck, CiGift, CiCircleCheck, CiBoxes } from "react-icons/ci";

const statusConfig = {
  pending: {
    label: "Awaiting Pickup",
    badgeCls: "bg-amber-50 text-amber-700 border-amber-200",
    accentCls: "border-l-amber-400",
  },
  collected: {
    label: "Collected",
    badgeCls: "bg-sky-50 text-sky-700 border-sky-200",
    accentCls: "border-l-sky-500",
  },
  enroute: {
    label: "En Route",
    badgeCls: "bg-violet-50 text-violet-700 border-violet-200",
    accentCls: "border-l-violet-600",
  },
  ready_for_collection: {
    label: "Ready for Collection",
    badgeCls: "bg-teal-50 text-teal-700 border-teal-200",
    accentCls: "border-l-teal-500",
  },
  delivered: {
    label: "Delivered",
    badgeCls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    accentCls: "border-l-emerald-500",
  },
};

function DetailItem({ label, value, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
        {label}
      </p>
      {children ?? <p className="text-sm text-gray-700 mt-0.5">{value}</p>}
    </div>
  );
}

export default function OrderCardTable({ order }) {
  const isLimpopo = order.parcel_order?.destination_province?.name === "Limpopo";

  const limpopoStatuses = { pending: 0, collected: 1, enroute: 2, ready_for_collection: 3, delivered: 4 };
  const gautengStatuses = { pending: 0, collected: 1, enroute: 2, delivered: 3 };

  const orderStatuses = isLimpopo ? limpopoStatuses : gautengStatuses;
  const deliveryStatus = order.parcel_order?.delivery_status ?? "pending";
  const step = orderStatuses[deliveryStatus] ?? 0;
  const config = statusConfig[deliveryStatus] ?? statusConfig.pending;

  const options = { day: "numeric", month: "short", year: "numeric" };
  const formattedDate = new Date(
    order.parcel_order.collection_date,
  ).toLocaleDateString("en-US", options);

  const needsPayment = order.parcel_order.status === "created";

  const StepDot = ({ icon: Icon, label, active }) => (
    <div className="flex flex-col items-center flex-shrink-0">
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${active ? "bg-violet-600 border-violet-600" : "bg-white border-gray-200"}`}
      >
        <Icon size={14} color={active ? "white" : "#d1d5db"} />
      </div>
      <span
        className={`text-xs mt-1 font-medium ${active ? "text-violet-700" : "text-gray-400"}`}
      >
        {label}
      </span>
    </div>
  );

  const Line = ({ active }) => (
    <div
      className={`flex-1 h-px mb-4 ${active ? "bg-violet-500" : "bg-gray-200"}`}
    />
  );

  return (
    <Disclosure>
      {({ open }) => (
        <div
          className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${config.accentCls} overflow-hidden`}
        >
          <Disclosure.Button className="w-full text-left px-5 pt-4 pb-3">
            {/* Route + meta + badge */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[15px] font-bold text-gray-900">
                    {order.parcel_order.departure_province.name}
                  </span>
                  <BsArrowRight
                    className="text-gray-400 flex-shrink-0"
                    size={13}
                  />
                  <span className="text-[15px] font-bold text-gray-900">
                    {order.parcel_order.destination_province.name}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formattedDate} ·{" "}
                  <span className="font-medium text-gray-500">
                    #{order.parcel_order.reference_number}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${config.badgeCls}`}
                >
                  {config.label}
                </span>
                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
              </div>
            </div>

            {/* Progress stepper */}
            <div className="flex items-center mt-4">
              <StepDot icon={CiGift} label="Collected" active={step >= 1} />
              <Line active={step >= 2} />
              <StepDot icon={CiDeliveryTruck} label="En Route" active={step >= 2} />
              {isLimpopo ? (
                <>
                  <Line active={step >= 3} />
                  <StepDot icon={CiBoxes} label="Ready" active={step >= 3} />
                  <Line active={step >= 4} />
                  <StepDot icon={CiCircleCheck} label="Delivered" active={step >= 4} />
                </>
              ) : (
                <>
                  <Line active={step >= 3} />
                  <StepDot icon={CiCircleCheck} label="Delivered" active={step >= 3} />
                </>
              )}
            </div>
          </Disclosure.Button>

          <Disclosure.Panel className="border-t border-gray-100 bg-gray-50/60 px-5 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              <DetailItem
                label="Delivery Type"
                value={`${order.parcel_order.price.price_type.name} · Collection`}
              />
              <DetailItem
                label="Collection Address"
                value={order.parcel_order.pickup_location}
              />
              <DetailItem label="Payment">
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm font-semibold text-gray-800">
                    R
                    {order.parcel_order.price.value *
                      order.parcel_order.quantity}
                  </span>
                  {needsPayment ? (
                    <span className="px-2.5 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                      Pending Payment
                    </span>
                  ) : (
                    <span className="px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                      {order.parcel_order.status
                        .replace(/_/g, " ")
                        .toUpperCase()}
                    </span>
                  )}
                </div>
              </DetailItem>
              <DetailItem label="Collection Date" value={formattedDate} />
              <DetailItem
                label="Delivery Address"
                value={order.parcel_order?.drop_off_location || order.parcel_order?.destination_town?.name || ""}
              />
              <DetailItem label="Tracking Number">
                <p className="text-sm font-mono text-gray-700 mt-0.5 tracking-wide">
                  {order.parcel_order.reference_number}
                </p>
              </DetailItem>
            </div>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
